import { format } from 'date-fns'
import {
  CALENDAR_COLOR_CLASSES,
  STAY_EDGE_INSET,
  getCalendarColorKey,
  parseStay,
  type CalendarReservation,
  type MonthBar,
} from '@/components/calendar/month-grid'
import { isReservationCancelled } from '@/lib/reservation-utils'
import { cn } from '@/lib/utils'

/**
 * A property's stays as month-grid bars, the way every cleaning-board calendar
 * draws them: one bar per live reservation, coloured by channel, cancelled ones
 * left off entirely.
 *
 * Shared between the job calendar (CAP-514) and the stay dialog: the same bar
 * must not mean one thing on one dialog and another on the next, and the
 * cancelled-stays-are-not-drawn rule is the kind that drifts when copied.
 *
 * Stays opt in to the Uplisting-style geometry (CAP-817): from 75% across the
 * check-in cell to 25% across the check-out cell, so same-day turnovers leave
 * a gap. Blocks and cleanings use the grid's default whole-column geometry.
 */

/**
 * The bar the operator came here about, drawn ringed.
 *
 * Inset: the grid clips its overflow, so an outer ring on a bar in the last
 * column ran past the grid's right border (CAP-812).
 */
export const STAY_FOCUS_RING = 'ring-2 ring-inset ring-mint'

/** The id a stay bar is keyed and focused on: `id`, falling back to `reservation_id`. */
export function stayBarId(reservation: CalendarReservation): string {
  return String(reservation.id || reservation.reservation_id || '')
}

interface StayBarOptions {
  /** The reservation to ring, if any. */
  focusedId?: string | null
  /**
   * Makes each bar a button opening that reservation. Left out, the bars are
   * labels: a stay with nothing to open is a fact, not a control.
   */
  onSelect?: (reservationId: string) => void
}

export function buildStayBars(
  reservations: CalendarReservation[],
  { focusedId = null, onSelect }: StayBarOptions = {}
): MonthBar[] {
  const bars: MonthBar[] = []
  for (const reservation of reservations) {
    if (isReservationCancelled(reservation)) continue
    const parsed = parseStay(reservation)
    if (!parsed) continue
    const id = stayBarId(reservation)
    const guest = reservation.guest_name || 'Guest'
    const span = `${format(parsed.checkInDate, 'd MMM')} to ${format(parsed.checkOutDate, 'd MMM')}`
    bars.push({
      key: `reservation-${id}`,
      start: parsed.checkInDate,
      endExclusive: parsed.checkOutDate,
      edgeInset: STAY_EDGE_INSET,
      continuesPastMonth: true,
      label: guest,
      title: `${guest}, ${span}`,
      testId: 'context-stay-bar',
      data: { 'data-reservation-id': id },
      className: cn(
        CALENDAR_COLOR_CLASSES[getCalendarColorKey(reservation)],
        focusedId === id && STAY_FOCUS_RING
      ),
      ...(onSelect && id
        ? {
            onClick: () => onSelect(id),
            ariaLabel: `Open stay: ${guest}, ${span}`,
          }
        : {}),
    })
  }
  return bars
}
